import { Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "./ThemeProvider";

interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const { resolvedTheme, toggleTheme } = useTheme();
  const isLight = resolvedTheme === "light";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isLight}
      aria-label={`Switch to ${isLight ? "dark" : "light"} mode`}
      onClick={toggleTheme}
      className={cn(
        "group relative inline-flex h-8 w-full shrink-0 cursor-pointer items-center rounded-full bg-sidebar-accent p-1 ring-1 ring-sidebar-border transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-sidebar",
        className
      )}
    >
      {/* Sliding knob — half the track width, slides between the two icon halves */}
      <span
        aria-hidden
        className={cn(
          "absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-card shadow-sm ring-1 ring-sidebar-border/60 transition-transform duration-200 ease-out",
          isLight ? "translate-x-0" : "translate-x-full"
        )}
      />

      {/* Sun half */}
      <span
        className={cn(
          "relative z-10 flex h-6 flex-1 items-center justify-center transition-colors",
          isLight ? "text-card-foreground" : "text-sidebar-foreground/55"
        )}
      >
        <Sun className="h-3.5 w-3.5" />
      </span>

      {/* Moon half */}
      <span
        className={cn(
          "relative z-10 flex h-6 flex-1 items-center justify-center transition-colors",
          isLight ? "text-sidebar-foreground/55" : "text-card-foreground"
        )}
      >
        <Moon className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}
